/** PCM plumbing for the realtime Kory voice socket: 16 kHz up, 24 kHz down. */

export const INPUT_RATE = 16000;
export const OUTPUT_RATE = 24000;

/**
 * Streaming resampler from the AudioContext rate (44.1/48 kHz) to 16 kHz
 * PCM16. Keeps its fractional read position across chunks so 128-sample
 * worklet frames join without clicks.
 */
export class Downsampler {
  private readonly step: number;
  private pos = 0;
  private tail = 0;

  constructor(inputRate: number) {
    this.step = inputRate / INPUT_RATE;
  }

  push(input: Float32Array): Int16Array {
    const out: number[] = [];
    // Index -1 is the previous chunk's last sample, for interpolation across the seam.
    const at = (i: number) => (i < 0 ? this.tail : input[i]);
    while (this.pos < input.length - 1) {
      const i = Math.floor(this.pos);
      const frac = this.pos - i;
      const sample = at(i) + (at(i + 1) - at(i)) * frac;
      out.push(Math.max(-1, Math.min(1, sample)) * 0x7fff);
      this.pos += this.step;
    }
    this.pos -= input.length;
    if (input.length) this.tail = input[input.length - 1];
    return Int16Array.from(out);
  }
}

/** Little-endian PCM16 bytes from the socket, as Float32 for an AudioBuffer. */
export function pcm16ToFloat32(bytes: ArrayBuffer): Float32Array<ArrayBuffer> {
  const view = new DataView(bytes);
  const out = new Float32Array(Math.floor(bytes.byteLength / 2));
  for (let i = 0; i < out.length; i++) out[i] = view.getInt16(i * 2, true) / 0x8000;
  return out;
}

/** Root-mean-square level of a frame, 0–1. */
export function rms(frame: Float32Array): number {
  let sum = 0;
  for (const sample of frame) sum += sample * sample;
  return frame.length ? Math.sqrt(sum / frame.length) : 0;
}

/** An AudioWorklet that posts each 128-sample mic frame to the main thread. */
export const CAPTURE_WORKLET = `
class KoryCapture extends AudioWorkletProcessor {
  process(inputs) {
    const channel = inputs[0] && inputs[0][0];
    if (channel) this.port.postMessage(channel.slice(0));
    return true;
  }
}
registerProcessor("kory-capture", KoryCapture);
`;
